import * as React from "react";
import {
  addDays,
  COUPON_MAX_PCT,
  istanbulMidnightUtc,
  isDay,
  MAX_COUPONS_PER_WRITE,
  type AdminCoupon,
  type CouponWrite,
} from "@ewn/shared";
import { createCoupons, listCoupons, listSettings, setCouponEnabled } from "@/api/admin";
import { SettingCard } from "@/components/SettingCard";
import { useAction, useAsync } from "@/lib/useAsync";
import { when } from "@/lib/format";
import { BelowCostConfirm, useBelowCostQuestion } from "@/components/BelowCostConfirm";
import { FilterChips } from "@/components/FilterChips";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Notice } from "@/components/ui/feedback";
import { Field, Input, Textarea } from "@/components/ui/input";
import { PageHeader } from "@/components/ui/page-header";
import { DataTable } from "@/components/ui/paged-list";
import { TD, TH, TR } from "@/components/ui/table";

/**
 * Coupons: a percentage off, added to the crypto discount and capped at 90%.
 *
 * A use is an order that carries the code and has not failed, so the count
 * here goes down again when such an order fails or runs out of time. Turning
 * a coupon off stops new orders using it; an order already priced with it
 * keeps its price.
 */
type Mode = "one" | "many";

const MODES = [
  { value: "one" as const, label: "One code" },
  { value: "many" as const, label: "Generate codes" },
];

interface Draft {
  mode: Mode;
  code: string;
  count: string;
  pct: string;
  maxUses: string;
  /** An Istanbul day, the last one the coupon works on; empty for never. */
  through: string;
  note: string;
}

const EMPTY: Draft = {
  mode: "one",
  code: "",
  count: "10",
  pct: "10",
  maxUses: "",
  through: "",
  note: "",
};

/** The form as the worker's write; the worker says what is wrong with it. */
function writeOf(draft: Draft): CouponWrite {
  const through = draft.through.trim();
  return {
    ...(draft.mode === "one" ? { code: draft.code } : { count: Number(draft.count) }),
    pct: Number(draft.pct),
    max_uses: draft.maxUses.trim() === "" ? null : Number(draft.maxUses),
    // Valid through the whole of that day in Istanbul: it ends at the next
    // day's Istanbul midnight.
    expires_at: isDay(through) ? istanbulMidnightUtc(addDays(through, 1)) : null,
    note: draft.note.trim() || null,
  };
}

export function CouponsScreen() {
  const { data, loading, error, reload } = useAsync(() => listCoupons(), []);
  // The switch that shows the coupon box at checkout, here as well as in
  // Settings: a coupon made while the box is off is a code nobody can use.
  const settings = useAsync(() => listSettings(), []);
  const field = settings.data?.settings.find((s) => s.key === "coupon_field_enabled");
  const [draft, setDraft] = React.useState<Draft>(EMPTY);
  const [made, setMade] = React.useState<string[] | null>(null);
  const { ask, question } = useBelowCostQuestion();
  const create = useAction();
  const toggle = useAction();

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));

  /** Making a coupon, or turning one on: either may be asked about a loss. */
  function asking(action: typeof create, write: (allowBelowCost: boolean) => Promise<unknown>) {
    void ask(action, write, () => reload());
  }

  function make(write: CouponWrite) {
    setMade(null);
    asking(
      create,
      async (allow) => {
        const answer = await createCoupons(write, allow);
        setMade(answer.codes);
        setDraft(EMPTY);
        return answer;
      },
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Coupons"
        description="A percentage off, added to the crypto discount, 90% at most."
      />

      {settings.error && <Notice tone="error">{settings.error}</Notice>}
      {field && (
        <div className="grid gap-4 lg:grid-cols-2">
          <SettingCard setting={field} onSaved={settings.reload} />
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>New coupon</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            <FilterChips options={MODES} value={draft.mode} onChange={(mode) => set({ mode })} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {draft.mode === "one" ? (
              <Field label="Code" hint="4 to 32 letters, digits and dashes" htmlFor="coupon-code">
                <Input
                  id="coupon-code"
                  value={draft.code}
                  onChange={(e) => set({ code: e.target.value })}
                />
              </Field>
            ) : (
              <Field
                label="How many codes"
                hint={`Up to ${MAX_COUPONS_PER_WRITE}`}
                htmlFor="coupon-count"
              >
                <Input
                  id="coupon-count"
                  type="number"
                  min={1}
                  max={MAX_COUPONS_PER_WRITE}
                  value={draft.count}
                  onChange={(e) => set({ count: e.target.value })}
                />
              </Field>
            )}
            <Field label="Discount, %" hint={`1 to ${COUPON_MAX_PCT}`} htmlFor="coupon-pct">
              <Input
                id="coupon-pct"
                type="number"
                min={1}
                max={COUPON_MAX_PCT}
                value={draft.pct}
                onChange={(e) => set({ pct: e.target.value })}
              />
            </Field>
            <Field label="Uses, each code" hint="Empty for no limit" htmlFor="coupon-uses">
              <Input
                id="coupon-uses"
                type="number"
                min={1}
                value={draft.maxUses}
                onChange={(e) => set({ maxUses: e.target.value })}
              />
            </Field>
            <Field
              label="Valid through"
              hint="An Istanbul day; empty for no end"
              htmlFor="coupon-through"
            >
              <Input
                id="coupon-through"
                type="date"
                value={draft.through}
                onChange={(e) => set({ through: e.target.value })}
              />
            </Field>
            <Field label="Note" hint="For you; customers never see it" htmlFor="coupon-note">
              <Input
                id="coupon-note"
                value={draft.note}
                onChange={(e) => set({ note: e.target.value })}
              />
            </Field>
          </div>
          {create.error && <Notice tone="error">{create.error}</Notice>}
          {made && (
            <div className="space-y-1">
              <Notice tone="success">
                {`Made ${made.length} ${made.length === 1 ? "coupon" : "coupons"}.`}
              </Notice>
              {made.length > 1 && (
                <Textarea
                  readOnly
                  aria-label="The codes made"
                  rows={Math.min(made.length, 8)}
                  className="font-mono text-xs"
                  value={made.join("\n")}
                />
              )}
            </div>
          )}
          <Button disabled={create.busy} onClick={() => make(writeOf(draft))}>
            {create.busy ? "Making…" : "Make"}
          </Button>
        </CardContent>
      </Card>

      {(error || toggle.error) && <Notice tone="error">{error ?? toggle.error}</Notice>}

      <DataTable
        columns={6}
        loading={loading}
        empty="No coupon yet."
        rows={data?.rows ?? []}
        head={
          <>
            <TH>Code</TH>
            <TH numeric>Discount</TH>
            <TH numeric>Used</TH>
            <TH>Ends</TH>
            <TH>State</TH>
            <TH />
          </>
        }
        renderRow={(row: AdminCoupon) => (
          <TR key={row.code}>
            <TD className="font-mono text-xs">
              {row.code}
              {row.note && <div className="font-sans text-muted-foreground">{row.note}</div>}
            </TD>
            <TD numeric>{row.pct}%</TD>
            <TD numeric>{row.max_uses === null ? row.uses : `${row.uses} / ${row.max_uses}`}</TD>
            <TD className="whitespace-nowrap text-muted-foreground">
              {row.expires_at ? when(row.expires_at) : "never"}
            </TD>
            <TD>
              <Badge color={row.enabled ? "green" : "gray"}>{row.enabled ? "On" : "Off"}</Badge>
            </TD>
            <TD>
              <Button
                size="sm"
                variant="outline"
                disabled={toggle.busy}
                onClick={() =>
                  asking(toggle, (allow) =>
                    setCouponEnabled(row.code, !row.enabled, allow),
                  )
                }
              >
                {row.enabled ? "Turn off" : "Turn on"}
              </Button>
            </TD>
          </TR>
        )}
      />

      <BelowCostConfirm {...question} busy={create.busy || toggle.busy} />
    </div>
  );
}
